import { memo, useCallback } from 'react';
import styled from '@emotion/styled';

import bp from '../../../../lib/cssMediaQuery';
import { useCurrentRoute } from '../../../../lib/useCurrentRoute';
import useTracking from '../../../../lib/useTracking';
import NavLink from '../NavLink';

import type { ThemeProps } from '@emotion/react';
import type { CSSObject } from '@emotion/styled';
import type { ParsedUrlQuery } from 'querystring';
import type { Wws_Page } from '../../../../types/graphql';

const defaultColors = {
  background: '#ffffff',
  nav: '#414042',
} as const;

interface LiteNavItemProps {
  active: boolean;
}

const LiteNavItem = styled("li")`
  span {
    color: ${defaultColors.nav};
    padding: 0.75rem 0;
    box-sizing: border-box;
    border-top: 1px solid transparent;
    font-size: 0.875rem;

    ${({ active, theme }: ThemeProps & LiteNavItemProps): string =>
      active
        ? `
    border-bottom: 1px solid ${
      theme.liteSite?.Nav?.Link?.color || defaultColors.nav
    };
  `
        : ''}

    ${({ theme }: ThemeProps): string => `
      ${bp['min768']} {
        :hover {
          border-bottom: 1px solid ${
            theme.liteSite?.Nav?.Link?.color || defaultColors.nav
          };
        }
      }
    `};

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.Nav?.Link}

    ${({
      active,
      theme,
    }: ThemeProps & LiteNavItemProps): CSSObject | undefined =>
      active ? theme.components.Nav?.LinkActive : undefined}
  }
`;

const LiteNavRsvpItem = styled("li")`
  /* for RSVP nav link */
  a {
  }

  span {
    height: initial;
    padding: 0.75rem 1.25rem;
    font-size: 0.875rem;
    color: ${defaultColors.background};
    background: ${defaultColors.nav};
    border: 1px solid ${defaultColors.nav};

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.components?.Button}

    ${({ theme }: ThemeProps): CSSObject | undefined =>
      theme.liteSite?.Nav?.Button}

    &:focus,
    &:hover {
      ${({ theme }: ThemeProps): CSSObject | undefined =>
        theme.components?.ButtonHover}

      ${({ theme }: ThemeProps): CSSObject | undefined =>
        theme.liteSite?.components?.ButtonHover}

      ${({ theme }: ThemeProps): CSSObject | undefined =>
        theme.liteSite?.Nav?.ButtonHover}
    }
  }
`;

const userDecisionArea = 'desktop nav';

interface NavListItemProps {
  query: ParsedUrlQuery;
  page: Partial<Wws_Page>;
}

function NavListItem({ page, query }: NavListItemProps) {
  const NavLinkWrap =
    page.type === 'RsvpNavItem' && page.routeName === 'rsvp'
      ? LiteNavRsvpItem
      : LiteNavItem;

  const { pathPrefix, slug } = query;

  const { title, routeName } = page;

  const href = `/${pathPrefix}/${slug}/${routeName}`;

  const currentRoute = useCurrentRoute();
  const trackGuest = useTracking('guest', currentRoute, userDecisionArea);
  const trackRsvp = useTracking('rsvp', currentRoute, userDecisionArea);

  const handleTracking = useCallback(
    (selection: string): void => {
      if (selection === 'rsvp') {
        trackRsvp(selection, '');
      }

      trackGuest(selection, '');
    },
    [trackGuest, trackRsvp]
  );

  return (
    <NavLinkWrap key={page.id} active={currentRoute === page.routeName}>
      <NavLink
        href={href}
        as={href}
        route={routeName || ''}
        title={title || ''}
        handleTracking={handleTracking}
        desktop
      />
    </NavLinkWrap>
  );
}

export default memo(NavListItem);
